/**
 * Review Marquee Section
 * ------------------------------------------------------------------------
 * - Duplicates the track content once so the CSS keyframe (-50% translate)
 *   loops seamlessly with no visible jump.
 * - Pauses the CSS animation on mouse hover (desktop) and resumes on
 *   mouse leave. Touch devices are left untouched so scrolling never stalls.
 * - No rAF loop is needed: the marquee motion itself is handled by a
 *   GPU-accelerated CSS animation (transform: translate3d), which is both
 *   smoother and cheaper than driving transforms from JS every frame.
 *   JS here only toggles a class, listens are passive, and there is no
 *   per-frame work, so there is nothing to leak or throttle.
 * ------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var SELECTOR_SECTION = '[data-review-marquee]';

  function initMarquee(section) {
    var viewport = section.querySelector('[data-marquee-viewport]');
    var track = section.querySelector('[data-marquee-track]');

    if (!viewport || !track || track.dataset.marqueeReady === 'true') {
      return;
    }

    // Duplicate the original set of items once. Because the CSS keyframe
    // animates from translate3d(0,0,0) to translate3d(-50%,0,0), and the
    // track now contains two identical copies back-to-back, the visual
    // result is a perfectly continuous, non-resetting loop.
    var originalItems = Array.prototype.slice.call(track.children);
    if (originalItems.length === 0) {
      return;
    }

    var fragment = document.createDocumentFragment();
    originalItems.forEach(function (item) {
      var clone = item.cloneNode(true);
      // Duplicated images are purely decorative/visual repeats; hide them
      // from assistive tech and keyboard focus to avoid duplicate announcements.
      clone.setAttribute('aria-hidden', 'true');
      var clonedImg = clone.querySelector('img');
      if (clonedImg) {
        clonedImg.setAttribute('alt', '');
        clonedImg.removeAttribute('loading'); // duplicates render immediately alongside originals
      }
      fragment.appendChild(clone);
    });
    track.appendChild(fragment);
    track.dataset.marqueeReady = 'true';

    // Pause on hover only for pointer devices that support hover (desktop).
    // Touch devices (coarse pointer / no hover) keep scrolling uninterrupted.
    var supportsHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    if (supportsHover) {
      var pause = function () {
        viewport.classList.add('is-paused');
      };
      var resume = function () {
        viewport.classList.remove('is-paused');
      };

      viewport.addEventListener('mouseenter', pause, { passive: true });
      viewport.addEventListener('mouseleave', resume, { passive: true });

      // Keyboard users tabbing into the marquee get the same pause benefit.
      viewport.addEventListener('focusin', pause);
      viewport.addEventListener('focusout', resume);
    }
  }

  function initAll() {
    var sections = document.querySelectorAll(SELECTOR_SECTION);
    sections.forEach(initMarquee);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init when a section is added/re-rendered in the Theme Editor.
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector(SELECTOR_SECTION);
    if (section) {
      initMarquee(section);
    }
  });
})();
